import type { AniListMedia } from '../types';

const ENDPOINT = 'https://graphql.anilist.co';

const SEARCH_QUERY = `
  query ($search: String) {
    Page(page: 1, perPage: 15) {
      media(search: $search, type: ANIME, sort: POPULARITY_DESC) {
        id
        idMal
        title { romaji english native }
        coverImage { large }
        status
        episodes
        nextAiringEpisode { episode airingAt }
        airingSchedule(notYetAired: true, page: 1, perPage: 1) {
          nodes { episode airingAt }
        }
      }
    }
  }
`;

const TRENDING_QUERY = `
  query ($season: MediaSeason, $year: Int) {
    Page(page: 1, perPage: 15) {
      media(season: $season, seasonYear: $year, type: ANIME, sort: POPULARITY_DESC) {
        id
        idMal
        title { romaji english native }
        coverImage { large }
        status
        episodes
        nextAiringEpisode { episode airingAt }
        airingSchedule(notYetAired: true, page: 1, perPage: 1) {
          nodes { episode airingAt }
        }
      }
    }
  }
`;

const DETAIL_QUERY = `
  query ($id: Int) {
    Media(id: $id, type: ANIME) {
      id
      idMal
      title { romaji english native }
      coverImage { large }
      bannerImage
      description(asHtml: false)
      genres
      studios(isMain: true) { nodes { name } }
      season
      seasonYear
      averageScore
      meanScore
      format
      duration
      source
      status
      episodes
      nextAiringEpisode { episode airingAt }
      airingSchedule(notYetAired: true, perPage: 1) {
        nodes { episode airingAt }
      }
      externalLinks { site url icon }
    }
  }
`;

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string }>;
}

async function graphql<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) {
    throw new Error(`AniList HTTP ${res.status}`);
  }
  const body = (await res.json()) as GraphQLResponse<T>;
  if (body.errors?.length) {
    throw new Error(body.errors.map((e) => e.message).join('; '));
  }
  if (!body.data) {
    throw new Error('AniList response missing data');
  }
  return body.data;
}

export type AniListSeason = 'WINTER' | 'SPRING' | 'SUMMER' | 'FALL';

export function currentSeason(date: Date = new Date()): { season: AniListSeason; year: number } {
  const m = date.getMonth(); // 0..11
  // AniList convention: Dec–Feb = WINTER (Dec rolls to next year)
  let season: AniListSeason;
  if (m === 11 || m <= 1) season = 'WINTER';
  else if (m <= 4) season = 'SPRING';
  else if (m <= 7) season = 'SUMMER';
  else season = 'FALL';
  const year = m === 11 ? date.getFullYear() + 1 : date.getFullYear();
  return { season, year };
}

export interface AniListClient {
  search(query: string): Promise<AniListMedia[]>;
  getDetails(id: number): Promise<AniListMedia | null>;
  trendingSeason(): Promise<AniListMedia[]>;
}

export function useAniList(): AniListClient {
  return {
    async search(query: string): Promise<AniListMedia[]> {
      if (!query.trim()) return [];
      const data = await graphql<{ Page: { media: AniListMedia[] } }>(SEARCH_QUERY, {
        search: query,
      });
      return data.Page.media;
    },
    async getDetails(id: number): Promise<AniListMedia | null> {
      const data = await graphql<{ Media: AniListMedia | null }>(DETAIL_QUERY, { id });
      return data.Media;
    },
    async trendingSeason(): Promise<AniListMedia[]> {
      const { season, year } = currentSeason();
      const data = await graphql<{ Page: { media: AniListMedia[] } }>(TRENDING_QUERY, {
        season,
        year,
      });
      return data.Page.media;
    },
  };
}

export function preferredTitle(media: AniListMedia): string {
  return (
    media.title.english ?? media.title.romaji ?? media.title.native ?? `Anime #${media.id}`
  );
}

const DAY_NAMES = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export function airingDayName(unixSec: number): string {
  const date = new Date(unixSec * 1000);
  return DAY_NAMES[date.getDay()]!;
}

export function airingTimeHHMM(unixSec: number): string {
  const date = new Date(unixSec * 1000);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
